import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ApiURl from "../controllers/Api";

const heroImages = [
  {
    src: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=900&auto=format&fit=crop",
    alt: "Diamond ring close-up",
    position: "center",
  },
  {
    src: "https://images.unsplash.com/photo-1777286492764-456e3530e34c?q=80&w=900&auto=format&fit=crop",
    alt: "Luxury jewellery display",
    position: "center",
  },
  {
    src: "https://images.unsplash.com/photo-1567567557645-8450247d194a?q=80&w=900&auto=format&fit=crop",
    alt: "Traditional gold bangles",
    position: "center",
  },
];

const MAROON_DEEP = "var(--user-maroon-deep, #1A0000)";
const GOLD = "var(--user-gold, #B29063)";
const GOLD_LIGHT = "var(--user-gold-soft, #D4AF87)";
const BACKGROUND = "var(--user-bg, #E8D9D5)";
const TEXT_DARK = "var(--user-text, #2B0000)";
const TEXT_SOFT = "var(--user-text-soft, #685E59)";

const fallbackHero = {
  eyebrowTop: "Signature",
  titleMain: "Gold",
  eyebrowBottom: "Timeless",
  titleAccent: "Stories.",
  leadText: "Jewellery crafted for celebrations, heirlooms and everyday grace.",
  bodyText:
    "Discover refined gold collections shaped with careful detailing, warm finishes and a timeless Indian sensibility. Every piece is created to become part of your most meaningful moments.",
  primaryButtonText: "Explore Collection",
  primaryButtonUrl: "/all-products",
  secondaryButtonText: "Our Story",
  secondaryButtonUrl: "/about-us",
};

export default function HomeHero() {
  const [hero, setHero] = useState(fallbackHero);
  const [dynamicImages, setDynamicImages] = useState(heroImages);

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getHomeHero.php`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted || !response?.success || !response?.data) return;
        const data = response.data;

        setHero({
          eyebrowTop: data.eyebrow_top || fallbackHero.eyebrowTop,
          titleMain: data.title_main || fallbackHero.titleMain,
          eyebrowBottom: data.eyebrow_bottom || fallbackHero.eyebrowBottom,
          titleAccent: data.title_accent || fallbackHero.titleAccent,
          leadText: data.lead_text || fallbackHero.leadText,
          bodyText: data.body_text || fallbackHero.bodyText,
          primaryButtonText: data.primary_button_text || fallbackHero.primaryButtonText,
          primaryButtonUrl: data.primary_button_url || fallbackHero.primaryButtonUrl,
          secondaryButtonText: data.secondary_button_text || fallbackHero.secondaryButtonText,
          secondaryButtonUrl: data.secondary_button_url || fallbackHero.secondaryButtonUrl,
        });

        setDynamicImages([
          {
            ...heroImages[0],
            src: data.image_one_url || heroImages[0].src,
            alt: data.title_main ? `${data.title_main} hero image` : heroImages[0].alt,
          },
          {
            ...heroImages[1],
            src: data.image_three_url || heroImages[1].src,
            alt: data.title_accent ? `${data.title_accent} hero image` : heroImages[1].alt,
          },
          {
            ...heroImages[2],
            src: data.image_two_url || heroImages[2].src,
            alt: data.eyebrow_bottom ? `${data.eyebrow_bottom} hero image` : heroImages[2].alt,
          },
        ]);
      })
      .catch((error) => {
        console.error("Unable to load Home Hero settings:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const images = useMemo(() => dynamicImages, [dynamicImages]);

  return (
    <>
      {/* =======================================================================
          MOBILE / TABLET LAYOUT — shown only BELOW 1024px (lg breakpoint)
          Matches reference screenshot: two rows, organic blob images,
          heading vertically centered next to each blob.
      ======================================================================= */}
      <section
        className="
          lg:hidden
          relative isolate overflow-hidden
          px-5 pt-8 pb-10
          sm:px-8 sm:pt-10 sm:pb-12
        "
        style={{ backgroundColor: BACKGROUND }}
      >
        {/* Soft luxury background, same treatment as desktop */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: `
              radial-gradient(
                circle at 12% 20%,
                rgba(255,255,255,0.9),
                transparent 28%
              ),
              radial-gradient(
                circle at 84% 72%,
                rgba(212,175,135,0.22),
                transparent 28%
              )
            `,
          }}
        />

        {/* ---------- Row 1: "Trending / Styles." text (left) + blob image (right) ---------- */}
        <div className="relative z-10 flex items-center gap-4 sm:gap-6">
          <div className="w-[40%] shrink-0 sm:w-[38%]">
            <p
              className="
                text-[12px] font-semibold uppercase
                tracking-[0.26em]
                sm:text-[15px]
                sm:tracking-[0.3em]
              "
              style={{ color: GOLD_LIGHT }}
            >
              {hero.eyebrowTop}
            </p>

            <h1
              className="
                mt-1 font-serif
                text-[38px] leading-[0.88]
                tracking-[-0.04em]
                sm:text-[54px]
              "
              style={{ color: TEXT_DARK }}
            >
              {hero.titleMain}
            </h1>
          </div>

          {/* Blob 1: flat-ish left, rounded top arc, big curve right, asymmetric bottom */}
          <div
            className="
              relative w-[60%] aspect-[4/5]
              overflow-hidden
              shadow-[0_20px_55px_rgba(43,0,0,0.18)]
              sm:w-[62%]
            "
            style={{
              borderRadius: "62% 38% 53% 47% / 60% 44% 56% 40%",
            }}
          >
            <img
              src={images[0].src}
              alt={images[0].alt}
              className="h-full w-full object-cover"
              style={{ objectPosition: images[0].position }}
            />
          </div>
        </div>

        {/* ---------- Row 2: blob image (left) + "Timeless / Prices." text (right) ---------- */}
        <div className="relative z-10 mt-6 flex items-center gap-4 sm:mt-8 sm:gap-6">
          {/* Blob 2: wide, near-straight top, huge bottom curve, left heavily rounded, right soft */}
          <div
            className="
              relative w-[60%] aspect-[6/5]
              overflow-hidden
              shadow-[0_24px_60px_rgba(43,0,0,0.2)]
              sm:w-[60%]
            "
            style={{
              borderRadius: "60% 40% 40% 60% / 18% 18% 82% 82%",
            }}
          >
            <img
              src={images[2].src}
              alt={images[2].alt}
              className="h-full w-full object-cover"
              style={{ objectPosition: images[2].position }}
            />
            <div
              className="
                pointer-events-none absolute inset-0
                bg-gradient-to-t
                from-black/10
                via-transparent
                to-white/5
              "
            />
          </div>

          <div className="w-[40%] shrink-0 text-right sm:w-[40%]">
            <p
              className="
                text-[12px] font-semibold uppercase
                tracking-[0.26em]
                sm:text-[15px]
                sm:tracking-[0.3em]
              "
              style={{ color: GOLD_LIGHT }}
            >
              {hero.eyebrowBottom}
            </p>

            <p
              className="
                mt-1 font-serif italic
                text-[32px] leading-[0.9]
                tracking-[-0.03em]
                sm:text-[46px]
              "
              style={{ color: TEXT_DARK }}
            >
              {hero.titleAccent}
            </p>
          </div>
        </div>

        {/* ---------- Lead text, body text, buttons ---------- */}
        <div className="relative z-10 mt-8 sm:mt-10">
          <p
            className="
              font-serif text-[19px]
              leading-[1.35]
              sm:text-[23px]
            "
            style={{ color: TEXT_DARK }}
          >
            {hero.leadText}
          </p>

          <p
            className="
              mt-4 text-[13px] leading-7
              sm:text-[15px]
            "
            style={{ color: TEXT_SOFT }}
          >
            {hero.bodyText}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link
              to={hero.primaryButtonUrl}
              className="
                group inline-flex items-center gap-3
                rounded-full px-6 py-3.5
                font-serif text-sm font-semibold
                shadow-[0_18px_40px_rgba(43,0,0,0.2)]
                transition-all duration-300
                hover:-translate-y-1
                hover:shadow-[0_24px_50px_rgba(43,0,0,0.27)]
              "
              style={{
                backgroundColor: MAROON_DEEP,
                  color: "var(--user-cream, #F5ECE7)",
              }}
            >
              <span>{hero.primaryButtonText}</span>
              <span
                className="
                  grid h-8 w-8 place-items-center
                  rounded-full
                  transition-transform duration-300
                "
                style={{
                  backgroundColor: GOLD_LIGHT,
                  color: MAROON_DEEP,
                }}
              >
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>

            <Link
              to={hero.secondaryButtonUrl}
              className="
                inline-flex items-center
                border-b pb-1
                font-serif text-sm font-semibold
                transition-colors duration-300
                hover:text-[#8B6A55]
              "
              style={{
                color: GOLD,
                borderColor: GOLD,
              }}
            >
              {hero.secondaryButtonText}
            </Link>
          </div>
        </div>
      </section>

      {/* =======================================================================
          DESKTOP LAYOUT — 1024px (lg) AND ABOVE — UNCHANGED, EXACTLY YOUR CODE
          Only addition: "hidden lg:block" so it doesn't render below 1024px.
      ======================================================================= */}
      <section
        className="
          hidden lg:block
          relative isolate
          min-h-[calc(100dvh-96px)]
          overflow-hidden
        "
        style={{
          backgroundColor: BACKGROUND,
        }}
      >
        {/* Soft luxury background */}

        <div
          className="pointer-events-none absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: `
              radial-gradient(
                circle at 12% 20%,
                rgba(255,255,255,0.9),
                transparent 28%
              ),
              radial-gradient(
                circle at 84% 72%,
                rgba(212,175,135,0.22),
                transparent 28%
              )
            `,
          }}
        />

        <div
          className="
            relative mx-auto grid
            min-h-[calc(100dvh-96px)]
            max-w-[1440px]
            items-center gap-12
            px-5 pb-10 pt-20

            sm:px-8
            md:px-10

            lg:grid-cols-[0.88fr_1.12fr]
            lg:gap-8
            lg:px-14
            lg:pb-8
            lg:pt-24

            xl:px-20
          "
        >
          {/* ================= LEFT CONTENT ================= */}

          <div
            className="
              relative z-20
              mx-auto w-full max-w-[650px]
              pt-8
              lg:mx-0 lg:pt-0
            "
          >
            <div className="relative pl-10 sm:pl-16 lg:pl-8 xl:pl-12">
              <p
                className="
                  text-[18px] font-semibold uppercase
                  tracking-[0.43em]
                  sm:text-[22px]
                  lg:text-[24px]
                "
                style={{
                  color: GOLD_LIGHT,
                }}
              >
                {hero.eyebrowTop}
              </p>

              <h1
                className="
                  mt-2 font-serif
                  text-[66px] leading-[0.86]
                  tracking-[-0.055em]
                  sm:text-[88px]
                  md:text-[102px]
                  lg:text-[94px]
                  xl:text-[112px]
                "
                style={{
                  color: TEXT_DARK,
                }}
              >
                {hero.titleMain}
              </h1>

              <div className="mt-4 flex items-start sm:mt-5">
                <div className="ml-[34%] w-[75%] sm:ml-[35%] sm:w-[70%] lg:ml-[36%]">
                  <p
                    className="
                      mb-1 text-[17px] font-semibold uppercase
                      tracking-[0.34em]
                      sm:text-[20px]
                      lg:text-[21px]
                    "
                    style={{
                      color: GOLD_LIGHT,
                    }}
                  >
                    {hero.eyebrowBottom}
                  </p>

                  <p
                    className="
                      font-serif text-[59px] italic
                      leading-[0.88]
                      tracking-[-0.045em]
                      sm:text-[80px]
                      md:text-[90px]
                      lg:text-[82px]
                      xl:text-[99px]
                    "
                    style={{
                      color: TEXT_DARK,
                    }}
                  >
                    {hero.titleAccent}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-9 max-w-[540px] pl-10 sm:pl-16 lg:pl-8 xl:pl-12 sm:mt-10 lg:mt-11">
              <p
                className="
                  font-serif text-[22px]
                  leading-[1.35]
                  sm:text-[27px]
                  lg:text-[25px]
                "
                style={{
                  color: TEXT_DARK,
                }}
              >
                {hero.leadText}
              </p>

              <p
                className="
                  mt-5 max-w-[500px]
                  text-[13px] leading-7
                  sm:text-[15px]
                "
                style={{
                  color: TEXT_SOFT,
                }}
              >
                {hero.bodyText}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link
                  to={hero.primaryButtonUrl}
                  className="
                    group inline-flex items-center gap-3
                    rounded-full px-6 py-3.5
                    font-serif text-sm font-semibold
                    shadow-[0_18px_40px_rgba(43,0,0,0.2)]
                    transition-all duration-300
                    hover:-translate-y-1
                    hover:shadow-[0_24px_50px_rgba(43,0,0,0.27)]
                  "
                  style={{
                    backgroundColor: MAROON_DEEP,
                    color: "var(--user-cream, #F5ECE7)",
                  }}
                >
                  <span>{hero.primaryButtonText}</span>

                  <span
                    className="
                      grid h-8 w-8 place-items-center
                      rounded-full
                      transition-transform duration-300
                    "
                    style={{
                      backgroundColor: GOLD_LIGHT,
                      color: MAROON_DEEP,
                    }}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>

                <Link
                  to={hero.secondaryButtonUrl}
                  className="
                    inline-flex items-center
                    border-b pb-1
                    font-serif text-sm font-semibold
                    transition-colors duration-300
                    hover:text-[#8B6A55]
                  "
                  style={{
                    color: GOLD,
                    borderColor: GOLD,
                  }}
                >
                  {hero.secondaryButtonText}
                </Link>
              </div>
            </div>
          </div>

          {/* ================= RIGHT IMAGE COMPOSITION ================= */}

          <div
            className="
              relative mx-auto
              h-[388px] w-full max-w-[612px]

              sm:h-[475px]

              lg:h-[446px]
              lg:max-w-none

              xl:h-[490px]
            "
          >
            {/* Decorative curved line */}

            <div
              className="
                pointer-events-none absolute
                right-[2%] top-[9%]
                hidden h-[76%] w-[72%]
                rounded-[50%]
                border border-white/25
                lg:block
              "
            />

            {/* Top circular image */}

            <div
              className="
                absolute left-[32%] top-[-12%] z-20
                h-[139px] w-[139px]
                overflow-hidden rounded-full
                shadow-[0_25px_65px_rgba(43,0,0,0.18)]

                sm:left-[32%]
                sm:top-[-10%]
                sm:h-[174px] sm:w-[174px]

                lg:left-[16%]
                lg:top-[-8%]
                lg:h-[171px] lg:w-[171px]

                xl:left-[14%]
                xl:top-[-8%]
                xl:h-[194px] xl:w-[194px]
              "
            >
              <img
                src={images[0].src}
                alt={images[0].alt}
                className="
                  h-full w-full object-cover
                "
                style={{
                  objectPosition: images[0].position,
                }}
              />
            </div>

            {/* Bottom-left pill image */}

            <div
              className="
                absolute bottom-[-18%] left-[32%] z-30
                h-[285px] w-[166px]
                overflow-hidden rounded-[180px]
                shadow-[0_28px_75px_rgba(43,0,0,0.21)]

                sm:left-[32%]
                sm:bottom-[-16%]
                sm:h-[359px]
                sm:w-[212px]

                lg:bottom-[-18%]
                lg:left-[15%]
                lg:h-[327px]
                lg:w-[191px]

                xl:bottom-[-18%]
                xl:h-[386px]
                xl:w-[224px]
                xl:left-[13%]
              "
            >
              <img
                src={images[2].src}
                alt={images[2].alt}
                className="
                  h-full w-full object-cover
                "
                style={{
                  objectPosition: images[2].position,
                }}
              />

              <div
                className="
                  pointer-events-none absolute inset-0
                  bg-gradient-to-t
                  from-black/10
                  via-transparent
                  to-white/5
                "
              />
            </div>

            {/* Right main arch image */}

            <div
              className="
                absolute bottom-[3%] right-[1%] z-10
                h-[316px] w-[207px]
                overflow-hidden
                rounded-t-[145px]
                rounded-b-[32px]
                shadow-[0_35px_90px_rgba(43,0,0,0.22)]

                sm:right-[1%]
                sm:h-[410px]
                sm:w-[279px]
                sm:rounded-t-[180px]
                sm:rounded-b-[42px]

                lg:bottom-[2%]
                lg:right-[0%]
                lg:h-[385px]
                lg:w-[261px]
                lg:rounded-t-[165px]
                lg:rounded-b-[38px]

                xl:right-[0%]
                xl:h-[439px]
                xl:w-[297px]
                xl:rounded-t-[190px]
                xl:rounded-b-[43px]
              "
            >
              <img
                src={images[1].src}
                alt={images[1].alt}
                className="
                  h-full w-full object-cover
                "
                style={{
                  objectPosition: images[1].position,
                }}
              />

              <div
                className="
                  pointer-events-none absolute inset-0
                  bg-gradient-to-t
                  from-black/10
                  via-transparent
                  to-white/5
                "
              />
            </div>
          </div>
        </div>

        {/* Bottom divider */}

        <div
          className="absolute inset-x-[5%] bottom-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(43,0,0,0.18), transparent)",
          }}
        />
      </section>
    </>
  );
}
